import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapContainer, TileLayer, Circle, CircleMarker, Polyline, Tooltip, Marker, Pane, useMap } from 'react-leaflet'
import { divIcon } from 'leaflet'
import { motion } from 'framer-motion'
import { usePredictionAll } from '../hooks/usePrediction'
import api from '../api/client'
import {
  formatTempF, formatTempRangeF, formatPrecipIn, formatSnowIn, formatWindMph,
  formatDistanceMi, celsiusToFahrenheit, kmhToMph,
} from '../utils/units.js'
import { trackEvent } from '../analytics/mixpanel.js'
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx'
import InfoTooltip from '../components/shared/Tooltip.jsx'
import { Info, CloudLightning } from 'lucide-react'

// ── Risk palette ──────────────────────────────────────────────────────────────
const RISK = {
  none:     { color: '#10b981', label: 'None',     bg: 'rgba(16,185,129,0.12)',  border: 'rgba(16,185,129,0.3)'  },
  low:      { color: '#06b6d4', label: 'Low',      bg: 'rgba(6,182,212,0.12)',   border: 'rgba(6,182,212,0.3)'   },
  moderate: { color: '#f59e0b', label: 'Moderate', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)'  },
  high:     { color: '#f97316', label: 'High',     bg: 'rgba(249,115,22,0.12)',  border: 'rgba(249,115,22,0.3)'  },
  severe:   { color: '#ef4444', label: 'Severe',   bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.3)'   },
}

const SEVERITY_COLOR = ['#10b981','#06b6d4','#f59e0b','#f97316','#ef4444','#8b5cf6']

const riskColor = level => RISK[level]?.color ?? '#64748b'

// ── Region definitions (mirror of backend REGIONS) ────────────────────────────
const REGIONS = {
  'East Coast': ['ME','NH','VT','MA','RI','CT','NY','NJ','PA','DE','MD','VA','NC','SC','GA','FL','WV','DC'],
  'Midwest':    ['OH','IN','IL','MI','WI','MN','IA','MO','ND','SD','NE','KS'],
  'South':      ['TX','OK','AR','LA','MS','AL','TN','KY'],
  'Mountain':   ['MT','ID','WY','CO','NM','AZ','UT','NV'],
  'Pacific':    ['WA','OR','CA','AK','HI'],
}
const STATE_TO_REGION = Object.fromEntries(
  Object.entries(REGIONS).flatMap(([region, states]) => states.map(s => [s, region]))
)
const REGION_ICONS = { 'East Coast': '🌊', 'Midwest': '🌾', 'South': '☀️', 'Mountain': '⛰️', 'Pacific': '🌁' }

// Geographic center of each region + radius in metres for the weather zone circle
const REGION_GEO = {
  'East Coast': { center: [38.5,  -77.5], radius: 650_000 },
  'Midwest':    { center: [42.0,  -89.0], radius: 620_000 },
  'South':      { center: [32.5,  -91.5], radius: 680_000 },
  'Mountain':   { center: [40.0, -111.5], radius: 650_000 },
  'Pacific':    { center: [44.5, -121.5], radius: 700_000 },
}

// Bounds that cover continental US + Alaska + Hawaii + Puerto Rico without world wrap
const NA_BOUNDS = [[8, -178], [73, -40]]

// ── Temperature helpers ───────────────────────────────────────────────────────
function tempColor(avgC) {
  if (avgC == null) return '#64748b'
  if (avgC <= -5)  return '#93c5fd'  // freezing — icy blue
  if (avgC <= 5)   return '#7dd3fc'  // cold — sky blue
  if (avgC <= 15)  return '#6ee7b7'  // cool — mint
  if (avgC <= 25)  return '#fbbf24'  // warm — amber
  return '#f97316'                    // hot — orange
}
function tempEmoji(avgC) {
  if (avgC == null) return '🌡️'
  if (avgC <= -5)  return '❄️'
  if (avgC <= 5)   return '🧊'
  if (avgC <= 15)  return '🌤️'
  if (avgC <= 25)  return '☀️'
  return '🔥'
}

// ── DC map marker ─────────────────────────────────────────────────────────────
function makeDcIcon(dcId, severity) {
  const color = SEVERITY_COLOR[Math.min(5, severity ?? 0)]
  return divIcon({
    html: `
      <div style="position:relative;width:36px;height:36px;display:flex;align-items:center;justify-content:center">
        <div style="position:absolute;inset:-4px;border-radius:50%;border:2px solid ${color};opacity:0.4;animation:ping 2s cubic-bezier(0,0,0.2,1) infinite"></div>
        <div style="width:36px;height:36px;border-radius:50%;background:rgba(10,15,30,0.92);border:2.5px solid ${color};
          display:flex;align-items:center;justify-content:center;
          box-shadow:0 0 16px ${color}55,0 2px 8px rgba(0,0,0,0.6);
          font-size:10px;font-weight:800;color:${color};font-family:Inter,sans-serif;letter-spacing:-0.5px">
          ${dcId}
        </div>
      </div>`,
    className: '',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  })
}

// ── Tooltip card ──────────────────────────────────────────────────────────────
function StoreTooltip({ store }) {
  const r = RISK[store.risk_level] ?? RISK.none
  return (
    <div style={{
      fontFamily: 'Inter, sans-serif',
      background: 'rgba(8,12,28,0.97)',
      border: `1px solid ${r.border}`,
      borderRadius: 10,
      padding: '10px 13px',
      minWidth: 220,
      boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px ${r.border}`,
      color: '#e2e8f0',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b', marginBottom: 3 }}>
        Store
      </div>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#f1f5f9', marginBottom: 6 }}>
        #{store.store_id} — {store.store_city}, {store.store_state}
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
        🚛 {store.dc_id} → {store.store_city} &nbsp;·&nbsp; {formatDistanceMi(store.distance_road_mi)} &nbsp;·&nbsp; {store.drive_time_hours?.toFixed(1)} hr
      </div>

      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: r.bg, border: `1px solid ${r.border}`,
        borderRadius: 6, padding: '3px 8px', marginBottom: 8,
      }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: r.color }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: r.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {r.label} risk
        </span>
        <span style={{ fontSize: 11, color: r.color, opacity: 0.7 }}>
          {(store.delay_score * 100).toFixed(0)}%
        </span>
      </div>

      {store.risk_factors && store.risk_factors !== 'clear' && (
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>☁ {store.risk_factors}</div>
      )}

      {store.effects?.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#475569', marginBottom: 4 }}>
            Expected effects
          </div>
          {store.effects.map((e, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#cbd5e1', marginBottom: 2 }}>
              <div style={{ width: 3, height: 3, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
              {e}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Fit map to all store + DC markers on first load ───────────────────────────
function FitBounds({ stores, dcConditions }) {
  const map = useMap()
  const fitted = useRef(false)

  // Keep Leaflet's internal canvas in sync with its container's size.
  // Without this, browser zoom changes (or any layout reflow that resizes the
  // container) leave the map rendering at the old size — the tiles disappear
  // and the user sees a dark void. invalidateSize() is cheap and doesn't
  // disturb the current center/zoom, so it's safe to call on every resize.
  useEffect(() => {
    const container = map.getContainer()
    const ro = new ResizeObserver(() => {
      map.invalidateSize()
    })
    ro.observe(container)
    return () => ro.disconnect()
  }, [map])

  useEffect(() => {
    if (fitted.current) return
    const points = []
    // Exclude ocean-crossing stores (Hawaii, Alaska, Puerto Rico) — they pull bounds too wide
    ;(stores ?? []).forEach(s => {
      if (s.store_lat != null && s.store_lon != null && s.route_source !== 'estimated') {
        points.push([s.store_lat, s.store_lon])
      }
    })
    Object.values(dcConditions ?? {}).forEach(dc => {
      if (dc.lat && dc.lon) points.push([dc.lat, dc.lon])
    })
    if (points.length === 0) return

    const lats = points.map(p => p[0])
    const lons = points.map(p => p[1])
    const bounds = [[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]]

    const container = map.getContainer()

    // The map sits inside a flex column whose height isn't resolved on the
    // first paint, so a single rAF can run before the container has any size
    // and Leaflet computes the zoom against a 0×0 canvas → falls back to the
    // default `zoom={3}`. Watch the container with a ResizeObserver and only
    // fit once it has real pixel dimensions.
    const performFit = () => {
      const { width, height } = container.getBoundingClientRect()
      if (width < 50 || height < 50) return false
      map.invalidateSize()
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 7 })
      fitted.current = true
      return true
    }

    if (performFit()) return

    const ro = new ResizeObserver(() => {
      if (performFit()) ro.disconnect()
    })
    ro.observe(container)
    return () => ro.disconnect()
  }, [map, stores, dcConditions])

  return null
}

// ── Timeline pill ─────────────────────────────────────────────────────────────
function DayPill({ dateStr, summary, dcConditions, selected, onClick }) {
  const d = new Date(dateStr + 'T12:00:00')
  const dayName = d.toLocaleDateString('en-US', { weekday: 'short' })
  const dayNum  = d.getDate()
  const affected = (summary?.severe ?? 0) + (summary?.high ?? 0) + (summary?.moderate ?? 0)
  const topLevel = summary?.severe > 0 ? 'severe' : summary?.high > 0 ? 'high' : summary?.moderate > 0 ? 'moderate' : 'none'
  const color = riskColor(topLevel)

  const dcList = dcConditions ? Object.values(dcConditions) : []
  const tempsWithData = dcList.filter(dc => dc.min_temp_c != null && dc.max_temp_c != null)
  const minTemp = tempsWithData.length > 0 ? Math.min(...tempsWithData.map(dc => dc.min_temp_c)) : null
  const maxTemp = tempsWithData.length > 0 ? Math.max(...tempsWithData.map(dc => dc.max_temp_c)) : null
  const avgTemp = minTemp != null ? (minTemp + maxTemp) / 2 : null
  const maxPrecip  = dcList.length > 0 ? Math.max(0, ...dcList.map(dc => dc.precip_mm    ?? 0)) : 0
  const maxSnow    = dcList.length > 0 ? Math.max(0, ...dcList.map(dc => dc.snowfall_cm  ?? 0)) : 0
  const maxWindKmh = dcList.length > 0 ? Math.max(0, ...dcList.map(dc => dc.max_wind_kmh ?? 0)) : 0

  const tColor = tempColor(avgTemp)
  const tEmoji = tempEmoji(avgTemp)
  const pEmoji = maxSnow > 0.5 ? '❄' : maxPrecip > 5 ? '🌧' : maxPrecip > 0 ? '💧' : null
  const pColor = maxSnow > 0.5 ? '#93c5fd' : '#60a5fa'
  const pLabel = maxSnow > 0.5 ? formatSnowIn(maxSnow) : maxPrecip > 0 ? formatPrecipIn(maxPrecip) : null
  const wLabel = kmhToMph(maxWindKmh) >= 10 ? formatWindMph(maxWindKmh) : null
  const DIM = 'rgba(255,255,255,0.1)'

  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.03, y: -2 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 380, damping: 22 }}
      style={{
        width: '100%',
        padding: '8px 10px',
        borderRadius: 10,
        background: selected ? 'rgba(0,212,255,0.08)' : 'rgba(255,255,255,0.03)',
        border: selected ? '1.5px solid rgba(0,212,255,0.55)' : '1.5px solid rgba(255,255,255,0.14)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        position: 'relative',
      }}
    >
      {/* ── Left column: day + indicator ── */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 34, flexShrink: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: selected ? '#00d4ff' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 1 }}>
          {dayName}
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: selected ? '#f1f5f9' : '#94a3b8', lineHeight: 1, marginBottom: 4 }}>
          {dayNum}
        </div>
        <div style={{ height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {affected > 0 ? (
            <div
              title={`${affected} route${affected !== 1 ? 's' : ''} with ${topLevel} or higher weather risk`}
              style={{ display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 9, color, fontWeight: 700 }}>{affected}</span>
            </div>
          ) : (
            <div title="All routes clear — no weather risk forecast"
              style={{ width: 5, height: 5, borderRadius: '50%', background: '#10b981' }}
            />
          )}
        </div>
      </div>

      {/* Divider */}
      <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />

      {/* ── Right column: 3 weather rows ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
        {/* Row 1 — temperature */}
        <div style={{ fontSize: 9, display: 'flex', alignItems: 'center', gap: 3, lineHeight: 1 }}>
          {minTemp != null ? (
            <>
              <span style={{ fontSize: 10, color: tColor, flexShrink: 0 }}>{tEmoji}</span>
              <span style={{ fontWeight: 700, color: tColor, whiteSpace: 'nowrap' }}>{formatTempRangeF(minTemp, maxTemp)}</span>
            </>
          ) : <span style={{ color: DIM }}>—</span>}
        </div>
        {/* Row 2 — precipitation / snow */}
        <div style={{ fontSize: 9, display: 'flex', alignItems: 'center', gap: 3, lineHeight: 1 }}>
          {pLabel ? (
            <>
              <span style={{ fontSize: 10, color: pColor, flexShrink: 0 }}>{pEmoji}</span>
              <span style={{ fontWeight: 700, color: pColor, whiteSpace: 'nowrap' }}>{pLabel}</span>
            </>
          ) : <span style={{ color: DIM }}>—</span>}
        </div>
        {/* Row 3 — wind */}
        <div style={{ fontSize: 9, display: 'flex', alignItems: 'center', gap: 3, lineHeight: 1 }}>
          {wLabel ? (
            <>
              <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>💨</span>
              <span style={{ fontWeight: 700, color: '#94a3b8', whiteSpace: 'nowrap' }}>{wLabel}</span>
            </>
          ) : <span style={{ color: DIM }}>—</span>}
        </div>
      </div>
    </motion.button>
  )
}

// ── Day pill briefing tooltip ─────────────────────────────────────────────────
function BriefingTooltip({ dateStr, briefingData, loading, summary }) {
  if (!dateStr) return null
  const d = new Date(dateStr + 'T12:00:00')
  const label = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
  const affected = (summary?.severe ?? 0) + (summary?.high ?? 0) + (summary?.moderate ?? 0)
  const topLevel = summary?.severe > 0 ? 'severe' : summary?.high > 0 ? 'high' : summary?.moderate > 0 ? 'moderate' : 'none'
  const borderColor = topLevel === 'none' ? 'rgba(16,185,129,0.3)' : `${riskColor(topLevel)}55`

  const briefing = briefingData?.briefing ?? null
  const regionRisks = briefingData?.region_risks ?? null

  const bullets = briefing
    ? briefing.split('\n').map(l => l.trim()).filter(l => l.startsWith('•'))
    : []

  // Top regions with affected routes
  const topRegions = regionRisks
    ? Object.entries(regionRisks)
        .filter(([, info]) => info.total > 0)
        .sort((a, b) => b[1].total - a[1].total)
        .slice(0, 5)
    : []

  return (
    <div style={{
      position: 'absolute',
      top: 'calc(100% + 10px)',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      width: 320,
      background: 'rgba(6,10,24,0.97)',
      backdropFilter: 'blur(16px)',
      border: `1px solid ${borderColor}`,
      borderRadius: 12,
      padding: '12px 14px',
      boxShadow: '0 16px 48px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
      fontFamily: 'Inter, sans-serif',
    }}>
      {/* Arrow */}
      <div style={{
        position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)',
        width: 10, height: 10, background: 'rgba(6,10,24,0.97)',
        border: `1px solid ${borderColor}`, borderRight: 'none', borderBottom: 'none',
        rotate: '45deg',
      }} />

      <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
        {label}
      </div>

      {affected > 0 && (
        <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
          {['severe','high','moderate'].map(lvl => summary?.[lvl] > 0 && (
            <span key={lvl} style={{
              fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5,
              background: `${riskColor(lvl)}18`, border: `1px solid ${riskColor(lvl)}44`,
              color: riskColor(lvl), textTransform: 'capitalize',
            }}>
              {summary[lvl]} {lvl}
            </span>
          ))}
        </div>
      )}

      {/* Regional risk mini-bars */}
      {topRegions.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 5 }}>
            By Region
          </div>
          {topRegions.map(([region, info]) => (
            <div key={region} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ fontSize: 11 }}>{REGION_ICONS[region] ?? '📍'}</span>
              <span style={{ fontSize: 10, color: '#64748b', width: 72, flexShrink: 0 }}>{region}</span>
              <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)' }}>
                <div style={{
                  height: '100%', borderRadius: 2,
                  width: `${Math.min(100, (info.total / (affected || 1)) * 100)}%`,
                  background: riskColor(info.top_level),
                  opacity: 0.8,
                }} />
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: riskColor(info.top_level), width: 24, textAlign: 'right' }}>
                {info.total}
              </span>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569', fontSize: 12 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid #1e3a5f', borderTopColor: '#38bdf8', animation: 'spin 0.8s linear infinite' }} />
          Generating briefing…
        </div>
      )}

      {!loading && bullets.length > 0 && (
        <>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', marginBottom: 8 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {bullets.map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11, color: '#cbd5e1', lineHeight: 1.5 }}>
                <span style={{ color: riskColor(topLevel), flexShrink: 0, marginTop: 1 }}>•</span>
                <span>{b.replace(/^•\s*/, '')}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && briefing && bullets.length === 0 && (
        <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>{briefing}</div>
      )}

      {!loading && briefingData === null && (
        <div style={{ fontSize: 11, color: '#ef4444' }}>Briefing unavailable</div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}

// ── DC condition card ─────────────────────────────────────────────────────────
function DcCard({ dcId, dc }) {
  const color = SEVERITY_COLOR[Math.min(5, dc.severity ?? 0)]
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${color}33`,
      borderRadius: 8,
      padding: '8px 10px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color, letterSpacing: '0.05em' }}>{dcId}</span>
        <span style={{ fontSize: 10, color: '#64748b' }}>{dc.name}</span>
      </div>
      <div style={{ fontSize: 11, color: '#cbd5e1', marginBottom: 3 }}>{dc.category}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {dc.snowfall_cm > 0 && <span style={{ fontSize: 10, color: '#93c5fd' }}>❄ {formatSnowIn(dc.snowfall_cm)}</span>}
        {dc.precip_mm   > 0 && <span style={{ fontSize: 10, color: '#60a5fa' }}>💧 {formatPrecipIn(dc.precip_mm)}</span>}
        {kmhToMph(dc.max_wind_kmh) > 12 && <span style={{ fontSize: 10, color: '#94a3b8' }}>💨 {formatWindMph(dc.max_wind_kmh)}</span>}
        {dc.min_temp_c != null && <span style={{ fontSize: 10, color: '#94a3b8' }}>{formatTempRangeF(dc.min_temp_c, dc.max_temp_c)}</span>}
      </div>
    </div>
  )
}

// ── Scoring empty state ───────────────────────────────────────────────────────
function ScoringEmptyState({ refetch }) {
  const [status, setStatus] = useState(null)   // null | scoring-status response
  const [starting, setStarting] = useState(false)
  const pollRef = useRef(null)

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null } }

  const startPolling = useCallback(() => {
    if (pollRef.current) return
    pollRef.current = setInterval(async () => {
      try {
        const { data: s } = await api.get('/predictions/scoring-status')
        setStatus(s)
        if (s.status === 'complete') { stopPolling(); refetch() }
        else if (s.status === 'error') stopPolling()
      } catch (_) {}
    }, 2000)
  }, [refetch])

  useEffect(() => {
    api.get('/predictions/scoring-status')
      .then(({ data: s }) => { setStatus(s); if (s.status === 'running') startPolling() })
      .catch(() => {})
    return stopPolling
  }, [startPolling])

  const handleCompute = async () => {
    setStarting(true)
    try {
      await api.post('/predictions/refresh')
      setStatus({ status: 'running', phase: 'starting', done: 0, total: 0 })
      startPolling()
    } catch (e) {
      setStatus({ status: 'error', error: e?.response?.data?.detail ?? e.message })
    } finally {
      setStarting(false)
    }
  }

  const isRunning = status?.status === 'running'
  const isError   = status?.status === 'error'
  const isDone    = status?.status === 'complete'

  const progressLabel = (() => {
    if (!isRunning) return null
    if (status.phase === 'fetching' && status.total > 0)
      return `Fetching weather data: ${status.done} of ${status.total} locations`
    if (status.phase === 'saving') return 'Saving scores to database…'
    return 'Starting…'
  })()

  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        maxWidth: 420, width: '100%', padding: '32px 28px', borderRadius: 16, textAlign: 'center',
        background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>📡</div>
        <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>
          No route score data available
        </h3>
        <p style={{ margin: '0 0 24px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
          Route scores haven't been computed yet or were cleared on the last server restart.
          Computing takes about 30 seconds.
        </p>

        {isError && (
          <div style={{
            marginBottom: 16, padding: '10px 14px', borderRadius: 8,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            color: '#f87171', fontSize: 12, textAlign: 'left',
          }}>
            <strong>Error:</strong> {status.error}
          </div>
        )}

        {isRunning ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 16, height: 16, borderRadius: '50%',
                border: '2px solid rgba(0,212,255,0.3)', borderTopColor: '#00d4ff',
                animation: 'spin 0.8s linear infinite',
              }} />
              <span style={{ fontSize: 13, color: '#94a3b8' }}>{progressLabel}</span>
            </div>
            {status.phase === 'fetching' && status.total > 0 && (
              <div style={{ height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 4,
                  background: 'linear-gradient(90deg,#0ea5e9,#00d4ff)',
                  width: `${Math.round((status.done / status.total) * 100)}%`,
                  transition: 'width 0.4s ease',
                }} />
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={handleCompute}
            disabled={starting || isDone}
            style={{
              padding: '10px 24px', borderRadius: 8, cursor: starting ? 'not-allowed' : 'pointer',
              background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.35)',
              color: '#00d4ff', fontSize: 13, fontWeight: 600,
              opacity: starting ? 0.6 : 1, transition: 'opacity 0.15s',
            }}
          >
            {starting ? 'Starting…' : isError ? 'Retry' : 'Compute Scores'}
          </button>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Prediction() {
  const navigate = useNavigate()
  const { data, isLoading, error, refetch } = usePredictionAll()

  // selectedDate is purely local — switching is instant, no network request
  const [selectedDate, setSelectedDate] = useState(null)

  const availableDates = data?.available_dates ?? []
  const activeDate = selectedDate ?? availableDates[0] ?? null

  const dayData   = activeDate ? (data?.by_date?.[activeDate] ?? null) : null
  const stores    = dayData?.stores ?? []
  const geometries = data?.geometries ?? {}

  const pillContainerRef = useRef(null)

  // ── Day pill hover briefings ──────────────────────────────────────────────
  const [hoveredDate, setHoveredDate] = useState(null)
  const hoverTimerRef = useRef(null)   // delay before showing
  const hideTimerRef  = useRef(null)   // delay before hiding (allows cursor to move to tooltip)
  const [briefings, setBriefings] = useState({})    // date → full API response
  const [briefingLoading, setBriefingLoading] = useState({})

  const fetchBriefing = async (dateStr) => {
    if (briefings[dateStr] !== undefined || briefingLoading[dateStr]) return
    setBriefingLoading(prev => ({ ...prev, [dateStr]: true }))
    try {
      const { data } = await api.get('/predictions/date-briefing', { params: { date: dateStr } })
      setBriefings(prev => ({ ...prev, [dateStr]: data }))
      console.log(`[Prediction] Briefing for ${dateStr}:`, data.briefing)
      console.log(`[Prediction] Region risks for ${dateStr}:`, data.region_risks)
    } catch (err) {
      console.warn('[Prediction] Briefing fetch failed:', err)
      setBriefings(prev => ({ ...prev, [dateStr]: null }))
    } finally {
      setBriefingLoading(prev => ({ ...prev, [dateStr]: false }))
    }
  }

  // Fire Briefing Viewed once per (date) — re-renders won't re-fire.
  const briefingsTrackedRef = useRef(new Set())
  useEffect(() => {
    if (!hoveredDate) return
    const b = briefings[hoveredDate]
    if (!b?.briefing) return
    if (briefingsTrackedRef.current.has(hoveredDate)) return
    briefingsTrackedRef.current.add(hoveredDate)
    trackEvent('Briefing Viewed', {
      surface: 'prediction',
      node_type: 'DC',
      briefing_length: b.briefing.length,
    })
  }, [hoveredDate, briefings])

  // ── Region filter ─────────────────────────────────────────────────────────
  const [selectedRegion, setSelectedRegion] = useState(null)

  // ── Weather heatmap (data-driven, changes with selected date) ────────────
  const [showWeather, setShowWeather] = useState(true)

  // ── Live radar (RainViewer — current conditions only, not date-aware) ────
  const [showRadar, setShowRadar] = useState(false)
  const [radarTileUrl, setRadarTileUrl] = useState(null)
  const [downloadingWeather, setDownloadingWeather] = useState(false)

  const downloadWeatherData = async () => {
    try {
      setDownloadingWeather(true)
      const response = await api.get('/predictions/weather-export', { responseType: 'blob' })
      const url = window.URL.createObjectURL(response.data)
      const disposition = response.headers?.['content-disposition'] || ''
      const filenameMatch = disposition.match(/filename=\"?([^"]+)\"?/)
      const filename = filenameMatch?.[1] || `aeo_weather_data_${new Date().toISOString().slice(0, 10)}.zip`

      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.warn('[Prediction] Weather export download failed:', err)
    } finally {
      setDownloadingWeather(false)
    }
  }

  useEffect(() => {
    fetch('https://api.rainviewer.com/public/weather-maps.json')
      .then(r => r.json())
      .then(d => {
        const frames = d?.radar?.past ?? []
        if (frames.length === 0) return
        const latest = frames[frames.length - 1]
        setRadarTileUrl(`https://tilecache.rainviewer.com${latest.path}/256/{z}/{x}/{y}/2/1_1.png`)
        console.log('[Prediction] RainViewer frame timestamp:', new Date(latest.time * 1000).toISOString())
      })
      .catch(err => console.warn('[Prediction] RainViewer fetch failed:', err))
  }, [])

  // ── Diagnostic console logs ────────────────────────────────────────────────
  useMemo(() => {
    if (!data) return
    console.group('[Prediction] Data loaded')
    console.log('Available dates:', data.available_dates)
    console.log('Geometry keys loaded:', Object.keys(data.geometries ?? {}).length)
    const sampleGeo = Object.entries(data.geometries ?? {}).slice(0, 2)
    sampleGeo.forEach(([k, v]) => console.log(`  geometry[${k}]: ${v?.length} points`))
    console.groupEnd()
  }, [data])

  useMemo(() => {
    if (!dayData || !activeDate) return
    const summary = dayData.summary ?? {}
    const byLevel = Object.entries(summary).map(([k, v]) => `${k}=${v}`).join(', ')
    console.group(`[Prediction] Date: ${activeDate}`)
    console.log('Risk summary:', byLevel)
    console.log('Total stores in payload:', stores.length)
    const withGeo = stores.filter(s => geometries[`${s.store_id}:${s.dc_id}`]).length
    const estimated = stores.filter(s => s.route_source === 'estimated').length
    console.log(`Routes with road geometry: ${withGeo} / ${stores.length}`)
    console.log(`Ocean-crossing (estimated, no line): ${estimated}`)
    console.log('Sample store record:', stores[0])
    console.groupEnd()
  }, [dayData, activeDate])

  // Per-region risk stats computed from active day stores.
  // top_level = worst store (used by filter buttons to flag any risk present).
  // median_level = median store risk (used by zone circles — avoids one outlier
  //   store painting an entire region red when most routes are clear).
  const regionStats = useMemo(() => {
    const order = { none: 0, low: 1, moderate: 2, high: 3, severe: 4 }
    const LEVELS = ['none', 'low', 'moderate', 'high', 'severe']
    const accumulated = {}
    const stats = {}
    for (const store of stores) {
      const region = STATE_TO_REGION[store.store_state]
      if (!region) continue
      if (!stats[region]) {
        stats[region] = { top_level: 'none', median_level: 'none', total: 0, affected: 0 }
        accumulated[region] = []
      }
      stats[region].total++
      if (store.risk_level !== 'none') stats[region].affected++
      if (order[store.risk_level] > order[stats[region].top_level]) {
        stats[region].top_level = store.risk_level
      }
      accumulated[region].push(order[store.risk_level])
    }
    for (const region of Object.keys(stats)) {
      const scores = [...accumulated[region]].sort((a, b) => a - b)
      const mid = Math.floor(scores.length / 2)
      const medianScore = scores.length % 2 === 0
        ? Math.round((scores[mid - 1] + scores[mid]) / 2)
        : scores[mid]
      stats[region].median_level = LEVELS[medianScore] ?? 'none'
    }
    return stats
  }, [stores])

  // Sort: none first (rendered below), severe last (rendered on top)
  // Also apply region filter when one is selected
  const sortedStores = useMemo(() => {
    const order = { none: 0, low: 1, moderate: 2, high: 3, severe: 4 }
    const filtered = selectedRegion
      ? stores.filter(s => STATE_TO_REGION[s.store_state] === selectedRegion)
      : stores
    return [...filtered].sort((a, b) => order[a.risk_level] - order[b.risk_level])
  }, [stores, selectedRegion])

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="flex flex-col gap-3 p-4 xl:p-6 min-h-[calc(100vh-4rem)] xl:h-[calc(100vh-4rem)] xl:overflow-hidden"
      style={{ background: 'linear-gradient(180deg,#060d1f 0%,#080f24 100%)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CloudLightning size={20} className="text-cyan-400" />
          Risk Prediction
          <InfoTooltip content="7-day weather delay risk across the store delivery network" placement="bottom" asButton>
            <Info size={15} className="text-slate-500 hover:text-slate-300 cursor-help transition-colors" />
          </InfoTooltip>
        </h1>
        {dayData && (
          <button
            onClick={downloadWeatherData}
            disabled={downloadingWeather}
            className="hidden xl:block"
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: downloadingWeather ? '#64748b' : '#38bdf8',
              background: 'rgba(56,189,248,0.10)',
              border: '1px solid rgba(56,189,248,0.35)',
              borderRadius: 8,
              padding: '5px 9px',
              cursor: downloadingWeather ? 'not-allowed' : 'pointer',
              flexShrink: 0,
            }}
          >
            {downloadingWeather ? 'Preparing ZIP…' : 'Download Weather Data'}
          </button>
        )}
      </div>

      {/* ── 7-day timeline ──────────────────────────────────────────────────
          Desktop (xl+, 1280+): single row, pills span the full width via
          flex + flex-1. Below xl: fluid grid — pills size between 115px
          and 1fr so they pack as many columns as fit without empty cells. */}
      {availableDates.length > 0 && (
        <div
          ref={pillContainerRef}
          className="grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(115px,1fr))] xl:flex"
        >
          {availableDates.map(d => (
            // Outer wrapper handles all hover logic so the tooltip stays open
            // when the cursor moves from the button into the tooltip area.
            <div
              key={d}
              className="xl:flex-1 relative"
              onMouseEnter={() => {
                clearTimeout(hideTimerRef.current)
                fetchBriefing(d)
                clearTimeout(hoverTimerRef.current)
                hoverTimerRef.current = setTimeout(() => setHoveredDate(d), 800)
              }}
              onMouseLeave={() => {
                clearTimeout(hoverTimerRef.current)
                // Small delay so cursor can travel to the tooltip without it disappearing
                hideTimerRef.current = setTimeout(() => setHoveredDate(null), 120)
              }}
            >
              <DayPill
                dateStr={d}
                summary={data?.by_date?.[d]?.summary ?? null}
                dcConditions={data?.by_date?.[d]?.dc_conditions ?? null}
                selected={d === activeDate}
                onClick={() => setSelectedDate(d)}
              />
              {hoveredDate === d && (
                // Cancel the hide timer when cursor enters the tooltip itself
                <div
                  onMouseEnter={() => clearTimeout(hideTimerRef.current)}
                  onMouseLeave={() => {
                    hideTimerRef.current = setTimeout(() => setHoveredDate(null), 120)
                  }}
                >
                  <BriefingTooltip
                    dateStr={d}
                    briefingData={briefings[d] ?? null}
                    loading={!!briefingLoading[d]}
                    summary={data?.by_date?.[d]?.summary ?? null}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Timeline legend ──────────────────────────────────────────────── */}
      {availableDates.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', paddingLeft: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>
            Legend
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
            <span style={{ fontSize: 10, color: '#64748b' }}>Clear — no weather risk</span>
          </div>
          {[
            { level: 'moderate', color: '#f59e0b', label: 'Moderate risk' },
            { level: 'high',     color: '#f97316', label: 'High risk' },
            { level: 'severe',   color: '#ef4444', label: 'Severe risk' },
          ].map(({ level, color, label }) => (
            <div key={level} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
              <span style={{ fontSize: 10, color: '#64748b' }}>{label}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: 6, borderLeft: '1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b' }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', marginLeft: 1 }}>3</span>
            <span style={{ fontSize: 10, color: '#475569' }}>= routes at moderate+ risk that day</span>
          </div>
          {dayData && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#475569', flexShrink: 0 }}>
              {stores.length} routes assessed &nbsp;·&nbsp; {activeDate}
            </span>
          )}
        </div>
      )}

      {/* ── Regional filter buttons ───────────────────────────────────────── */}
      {stores.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Object.entries(REGIONS).map(([region]) => {
            const stats = regionStats[region]
            const topLevel = stats?.top_level ?? 'none'
            const isActive = selectedRegion === region
            const color = riskColor(topLevel)
            return (
              <button
                key={region}
                onClick={() => {
                  // Only fire on activation, not on click-to-clear.
                  if (!isActive) {
                    trackEvent('Risk Level Inspected', {
                      level_clicked: 'region',
                      risk_level: topLevel,
                      region,
                    })
                  }
                  setSelectedRegion(isActive ? null : region)
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 11px', borderRadius: 8, cursor: 'pointer',
                  background: isActive ? `${color}18` : 'rgba(255,255,255,0.03)',
                  border: isActive ? `1px solid ${color}55` : '1px solid rgba(255,255,255,0.07)',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 13 }}>{REGION_ICONS[region]}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: isActive ? color : '#64748b' }}>
                  {region}
                </span>
                {stats?.affected > 0 && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, color,
                    background: `${color}18`, border: `1px solid ${color}33`,
                    borderRadius: 4, padding: '1px 5px',
                  }}>
                    {stats.affected}
                  </span>
                )}
              </button>
            )
          })}
          {selectedRegion && (
            <button
              onClick={() => setSelectedRegion(null)}
              style={{
                padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                fontSize: 11, color: '#475569',
              }}
            >
              ✕ Clear
            </button>
          )}
        </div>
      )}

      {isLoading && (
        <div className="flex-1 flex items-center justify-center min-h-[300px]">
          <LoadingSpinner text="Loading prediction data..." size="lg" />
        </div>
      )}

      {error && (
        <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, color: '#f87171', fontSize: 13 }}>
          Failed to load predictions: {error.message}
        </div>
      )}

      {!isLoading && !error && availableDates.length === 0 && (
        <ScoringEmptyState refetch={refetch} />
      )}

      {/* ── Map ──────────────────────────────────────────────────────────── */}
      {data && dayData && (
        <div
          className="relative h-[500px] xl:h-auto xl:flex-1 xl:min-h-0"
          style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          <style>{`
            @keyframes ping { 0%{transform:scale(1);opacity:0.4} 75%,100%{transform:scale(1.8);opacity:0} }
            .leaflet-tooltip { background:transparent!important; border:none!important; box-shadow:none!important; padding:0!important; }
            .leaflet-tooltip::before { display:none!important; }
          `}</style>

          <MapContainer
            center={[39.5, -98]}
            zoom={3}
            style={{ width: '100%', height: '100%' }}
            maxBounds={NA_BOUNDS}
            maxBoundsViscosity={1.0}
            zoomControl={true}
            scrollWheelZoom={true}
            zoomDelta={0.5}
            zoomSnap={0.25}
            wheelPxPerZoomLevel={120}
          >
            <FitBounds stores={sortedStores} dcConditions={dayData.dc_conditions} />
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; OpenStreetMap contributors &copy; CARTO'
              maxZoom={19}
            />

            {/* Weather zone overlay — one circle per region, never additive.
                Uses Circle (metre-radius) so zones are geographically sized.
                Keyed to activeDate so React replaces them on every date switch. */}
            {showWeather && (
              <Pane name="weather-zones" style={{ zIndex: 250 }}>
                {Object.entries(REGION_GEO).map(([region, geo]) => {
                  const stats = regionStats[region]
                  const level = stats?.median_level ?? 'none'
                  if (level === 'none') return null
                  const fillOpacity = level === 'low' ? 0.09 : level === 'moderate' ? 0.14 : level === 'high' ? 0.18 : 0.22
                  return (
                    <Circle
                      key={`zone-${region}-${activeDate}`}
                      center={geo.center}
                      radius={geo.radius}
                      pathOptions={{
                        color: riskColor(level),
                        weight: 1,
                        opacity: 0.25,
                        fillColor: riskColor(level),
                        fillOpacity,
                      }}
                      interactive={false}
                    />
                  )
                })}
              </Pane>
            )}

            {/* Live radar tile (RainViewer) — current conditions only, not date-aware */}
            {showRadar && radarTileUrl && (
              <TileLayer url={radarTileUrl} opacity={0.50} zIndex={210} />
            )}

            {/* Route paths — all routes shown; none=faint network, elevated=highlighted */}
            {/* Ocean-crossing routes (estimated) are always skipped — no line over water */}
            {sortedStores.map(store => {
              // Skip ocean crossings — no road geometry exists, line would cross open water
              if (store.route_source === 'estimated') return null

              const level = store.risk_level
              const isNone = level === 'none'

              const color   = isNone ? '#1e3a5f' : riskColor(level)
              const opacity = isNone ? 0.45 : level === 'low' ? 0.50 : level === 'moderate' ? 0.65 : 0.85
              const weight  = isNone ? 1 : level === 'severe' ? 2.5 : level === 'high' ? 2 : 1.5
              const dashed  = level === 'low' ? '4 6' : undefined

              const geoKey = `${store.store_id}:${store.dc_id}`
              // geometry is [[lat,lon],...] from pipeline; fall back to straight line
              const positions = geometries[geoKey]
                ?? [[store.dc_lat, store.dc_lon], [store.store_lat, store.store_lon]]

              // No tooltip on the route polyline — the store marker tooltip
              // carries the same payload, and attaching tooltips to both
              // layers caused overlap when the cursor crossed a route and
              // its store marker at the same time (TECH-31).
              return (
                <Polyline
                  key={`route-${store.store_id}`}
                  positions={positions}
                  pathOptions={{ color, weight, opacity, dashArray: dashed }}
                />
              )
            })}

            {/* Store markers */}
            {sortedStores.map(store => {
              const level = store.risk_level
              const color = riskColor(level)
              const radius      = level === 'severe' ? 7 : level === 'high' ? 6 : level === 'moderate' ? 5 : 4
              const fillOpacity = level === 'none' ? 0.20 : level === 'low' ? 0.45 : 0.75

              return (
                <CircleMarker
                  key={`store-${store.store_id}`}
                  center={[store.store_lat, store.store_lon]}
                  radius={radius}
                  pathOptions={{
                    color,
                    weight: 1.5,
                    fillColor: color,
                    fillOpacity,
                    opacity: level === 'none' ? 0.25 : 0.9,
                    // Indicate clickable on hover (RF-T05)
                    className: 'cursor-pointer',
                  }}
                  // RF-T05 — open the store detail page on marker click.
                  // Guard against missing IDs to satisfy the fallback acceptance criterion.
                  eventHandlers={{
                    click: () => {
                      if (store.store_id == null) {
                        console.warn('Map marker missing store_id; cannot navigate', store)
                        return
                      }
                      // Fire BEFORE navigation; the subsequent Store Drilldown is a separate event.
                      trackEvent('Risk Level Inspected', {
                        level_clicked: 'store',
                        risk_level: store.risk_level,
                        region: STATE_TO_REGION[store.store_state],
                      })
                      navigate(`/stores/${store.store_id}`)
                    },
                  }}
                >
                  <Tooltip sticky direction="top" offset={[0, -6]}>
                    <StoreTooltip store={store} />
                  </Tooltip>
                </CircleMarker>
              )
            })}

            {/* DC markers */}
            {Object.entries(dayData.dc_conditions).map(([dcId, dc]) => {
              if (!dc.lat || !dc.lon) return null
              return (
                <Marker
                  key={dcId}
                  position={[dc.lat, dc.lon]}
                  icon={makeDcIcon(dcId, dc.severity)}
                  // RF-T05 — there's no dedicated DC detail page, so route to the
                  // Allocation table filtered by the DC. If the dcId is empty for any
                  // reason, swallow the click rather than navigating to a broken URL.
                  eventHandlers={{
                    click: () => {
                      if (!dcId) {
                        console.warn('Map marker missing dc id; cannot navigate', dc)
                        return
                      }
                      navigate(`/allocation?dc=${encodeURIComponent(dcId)}`)
                    },
                  }}
                >
                  <Tooltip sticky direction="top" offset={[0, -20]}>
                    <div style={{
                      fontFamily: 'Inter,sans-serif',
                      background: 'rgba(8,12,28,0.97)',
                      border: '1px solid rgba(0,212,255,0.25)',
                      borderRadius: 10,
                      padding: '10px 14px',
                      color: '#e2e8f0',
                      minWidth: 180,
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#00d4ff', marginBottom: 4 }}>
                        DC {dcId} — {dc.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{dc.category}</div>
                      {dc.snowfall_cm > 0 && <div style={{ fontSize: 11, color: '#93c5fd' }}>❄ Snow: {formatSnowIn(dc.snowfall_cm)}</div>}
                      {dc.precip_mm   > 0 && <div style={{ fontSize: 11, color: '#60a5fa' }}>💧 Precip: {formatPrecipIn(dc.precip_mm)}</div>}
                      {kmhToMph(dc.max_wind_kmh) > 12 && <div style={{ fontSize: 11, color: '#94a3b8' }}>💨 Wind: {formatWindMph(dc.max_wind_kmh)}</div>}
                      {dc.min_temp_c != null && <div style={{ fontSize: 11, color: '#94a3b8' }}>🌡 {formatTempRangeF(dc.min_temp_c, dc.max_temp_c)}</div>}
                    </div>
                  </Tooltip>
                </Marker>
              )
            })}
          </MapContainer>

          {/* ── Map overlay toggles ──────────────────────────────────────── */}
          <div style={{
            position: 'absolute', bottom: 36, left: 12, zIndex: 1000,
            display: 'flex', flexDirection: 'column', gap: 5,
          }}>
            {/* Weather heatmap toggle — date-aware, uses our forecast data */}
            <button
              onClick={() => setShowWeather(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: showWeather ? 'rgba(249,115,22,0.15)' : 'rgba(6,10,22,0.82)',
                border: showWeather ? '1px solid rgba(249,115,22,0.45)' : '1px solid rgba(255,255,255,0.10)',
                borderRadius: 8, padding: '6px 11px', cursor: 'pointer',
                backdropFilter: 'blur(8px)', transition: 'all 0.18s',
              }}
            >
              <span style={{ fontSize: 13 }}>🌡</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: showWeather ? '#fb923c' : '#64748b' }}>
                Median risk zones
              </span>
            </button>

            {/* Live radar toggle — real precipitation radar, always current */}
            <button
              onClick={() => setShowRadar(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: showRadar ? 'rgba(0,180,255,0.15)' : 'rgba(6,10,22,0.82)',
                border: showRadar ? '1px solid rgba(0,180,255,0.45)' : '1px solid rgba(255,255,255,0.10)',
                borderRadius: 8, padding: '6px 11px', cursor: 'pointer',
                backdropFilter: 'blur(8px)', transition: 'all 0.18s',
              }}
            >
              <span style={{ fontSize: 13 }}>🌧</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: showRadar ? '#38bdf8' : '#64748b' }}>
                {radarTileUrl ? 'Live radar' : 'Live radar…'}
              </span>
            </button>
          </div>

          {/* ── Floating sidebar ─ hidden below xl so it doesn't obscure the map at narrow widths or zoomed-in desktop ── */}
          <div className="hidden xl:block" style={{
            position: 'absolute', top: 12, right: 12, zIndex: 1000,
            width: 200,
            background: 'rgba(6,10,22,0.92)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12,
            padding: '12px 14px',
            pointerEvents: 'none',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
              Risk Summary
            </div>
            {['severe','high','moderate','low','none'].map(level => {
              const count = dayData.summary[level] ?? 0
              if (count === 0) return null
              return (
                <div key={level} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: riskColor(level) }} />
                    <span style={{ fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{level}</span>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: riskColor(level) }}>{count}</span>
                </div>
              )
            })}

            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '10px 0' }} />
            <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              DC Conditions
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {Object.entries(dayData.dc_conditions).map(([dcId, dc]) => (
                <DcCard key={dcId} dcId={dcId} dc={dc} />
              ))}
            </div>

            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '10px 0' }} />
            <div style={{ fontSize: 10, color: '#334155', lineHeight: 1.8 }}>
              <div>● Store &nbsp; — Route</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                {['severe','high','moderate','low'].map(l => (
                  <span key={l} style={{ color: riskColor(l), fontSize: 9, textTransform: 'capitalize' }}>{l[0].toUpperCase()}</span>
                ))}
              </div>
              <div style={{ marginTop: 6, fontSize: 9, color: '#334155', lineHeight: 1.6 }}>
                Zone shading = <span style={{ color: '#475569' }}>median</span> store risk.<br />
                Individual dots show each store's actual risk.
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}
